import { useRef } from 'react';
import { Button } from '@/components/ui/Button';
import type { IndividualReport } from './api';

interface IndividualReportPdfModalProps {
  open: boolean;
  onClose: () => void;
  data: IndividualReport;
}

const DEFAULT_BANDS = [
  { name: 'Pelita Literasi', range: '86 – 100', desc: 'Menginspirasi dan membangun ekosistem literasi melalui kepemimpinan, kolaborasi, dan pemberdayaan sehingga tercipta gerakan literasi yang berdampak dan berkelanjutan.' },
  { name: 'Penggerak Literasi', range: '71 – 85', desc: 'Menggerakkan individu, kelompok, dan berbagai pihak untuk terlibat aktif dalam program literasi serta mendorong tumbuhnya kolaborasi di wilayahnya.' },
  { name: 'Pengamal Literasi', range: '51 – 70', desc: 'Menerapkan pengetahuan dan kompetensi PELITA SLI dalam melaksanakan program, mendampingi masyarakat, dan menjalankan perannya secara konsisten.' },
  { name: 'Pecinta Literasi', range: '26 – 50', desc: 'Berpartisipasi secara aktif dalam kegiatan literasi dan menunjukkan kepedulian terhadap pengembangan TBM serta masyarakat.' },
  { name: 'Pemula', range: '< 26', desc: 'Memahami dasar-dasar literasi serta peran PELITA SLI, dan mulai mengembangkan kompetensi untuk berkontribusi dalam kegiatan literasi.' },
];

export function IndividualReportPdfModal({
  open,
  onClose,
  data,
}: IndividualReportPdfModalProps) {
  const printAreaRef = useRef<HTMLDivElement>(null);

  if (!open) return null;

  const { individual, latest, dimensions, trend, bands } = data;
  if (!latest) return null;

  const handlePrint = () => {
    window.print();
  };

  const regionText = individual.regionPath?.map((r) => r.name).join(' → ') || '—';

  // Standar definisi kategori dari instrumen atau fallback
  const bandList = bands && bands.length > 0
    ? bands.map((b) => ({
        name: b.name,
        range: b.display || (b.gt !== null ? `> ${b.gt}` : '≤ batas bawah'),
        desc: b.characteristic || DEFAULT_BANDS.find((d) => d.name.toLowerCase() === b.name.toLowerCase())?.desc || '—',
      }))
    : DEFAULT_BANDS;

  const currentBandDef = bandList.find(
    (b) => b.name.toLowerCase() === latest.band?.toLowerCase(),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4 sm:p-6 backdrop-blur-xs print:static print:p-0 print:bg-white print:overflow-visible">
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-2xl overflow-hidden print:max-h-none print:shadow-none print:m-0 print:p-0 print:border-none print:w-full">
        {/* Modal Toolbar (hidden when printing) */}
        <div className="no-print flex items-center justify-between border-b border-border bg-subtle px-6 py-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">📄</span>
            <div>
              <h3 className="text-base font-bold text-ink">Pratinjau Cetak / PDF Rapor Individu</h3>
              <p className="text-xs text-ink-secondary">{individual.code} · {individual.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onClose}>
              Tutup
            </Button>
            <Button onClick={handlePrint}>
              🖨️ Cetak / Simpan PDF
            </Button>
          </div>
        </div>

        {/* Printable Report Document */}
        <div
          ref={printAreaRef}
          className="printable-report flex-1 overflow-y-auto p-8 sm:p-12 text-ink print:overflow-visible print:p-0 print:text-black"
        >
          {/* Header Kop Resmi */}
          <div className="border-b-2 border-primary pb-5 mb-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3.5">
                <img src="/favicon.png" alt="Logo LENTERA" className="size-12 object-contain" />
                <div>
                  <h2 className="text-base font-bold tracking-tight text-ink uppercase">
                    Sekolah Literasi Indonesia
                  </h2>
                  <p className="text-xs text-ink-secondary">
                    LENTERA — Sistem Monitoring & Evaluasi Program Literasi Komunitas
                  </p>
                </div>
              </div>
              <div className="text-right text-xs text-ink-secondary">
                <span className="inline-block px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 mb-1">
                  Dokumen Resmi
                </span>
                <p className="tabular">Dicetak: {new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h1 className="text-xl sm:text-2xl font-black text-ink tracking-tight uppercase">
                  Rapor Capaian Kompetensi PELITA SLI
                </h1>
                <p className="text-xs text-ink-secondary">
                  Periode Evaluasi: <strong className="text-ink">{latest.periodName}</strong>
                </p>
              </div>
              <div className="text-left sm:text-right">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                  <span className="size-1.5 rounded-full bg-emerald-600" />
                  Status: {latest.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Bagian Identitas Individu */}
          <div className="rounded-lg border border-border bg-subtle/30 p-5 mb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
              {individual.photoKey ? (
                <img
                  src={individual.photoKey.startsWith('http') ? individual.photoKey : `/api/v1/files/${individual.photoKey}`}
                  alt={individual.name}
                  className="size-24 rounded-full object-cover border-2 border-primary shadow-xs shrink-0"
                />
              ) : (
                <div className="size-24 rounded-full bg-primary-soft text-primary font-bold text-2xl flex items-center justify-center border-2 border-primary shadow-xs shrink-0">
                  {individual.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 w-full text-center sm:text-left">
                <div className="inline-block px-2.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-primary text-white mb-1.5">
                  {individual.code}
                </div>
                <h2 className="text-xl font-bold text-ink">{individual.name}</h2>
                {individual.personalBranding && (
                  <p className="text-sm font-medium text-primary mt-0.5">{individual.personalBranding}</p>
                )}
                {individual.profession && (
                  <p className="text-xs text-ink-secondary">
                    {individual.profession}
                    {individual.lastEducation && ` · ${individual.lastEducation}`}
                  </p>
                )}

                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4 text-xs border-t border-border/80 pt-3 text-left">
                  <div>
                    <span className="text-ink-muted block text-[11px]">TBM Induk:</span>
                    <strong className="text-ink font-semibold">
                      {individual.tbmName ? `${individual.tbmCode ? `${individual.tbmCode} - ` : ''}${individual.tbmName}` : '—'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-ink-muted block text-[11px]">Wilayah Penugasan:</span>
                    <strong className="text-ink font-semibold">{regionText}</strong>
                  </div>
                  <div>
                    <span className="text-ink-muted block text-[11px]">Tempat, Tgl Lahir:</span>
                    <span className="text-ink">
                      {[individual.birthPlace, individual.birthDate].filter(Boolean).join(', ') || '—'}
                    </span>
                  </div>
                  {individual.phone && (
                    <div>
                      <span className="text-ink-muted block text-[11px]">No. Telepon / WA:</span>
                      <span className="text-ink">{individual.phone}</span>
                    </div>
                  )}
                  {individual.email && (
                    <div>
                      <span className="text-ink-muted block text-[11px]">Email:</span>
                      <span className="text-ink">{individual.email}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-ink-muted block text-[11px]">Jenis Kelamin:</span>
                    <span className="text-ink">{individual.gender === 'L' ? 'Laki-laki' : individual.gender === 'P' ? 'Perempuan' : '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Hero Banner Capaian Level */}
          <div className="rounded-xl border-2 border-primary/30 bg-primary-soft/20 p-5 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  Indeks Capaian Kompetensi
                </span>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="text-4xl font-black text-ink tabular">
                    {latest.index !== null ? latest.index.toFixed(1) : '—'}
                  </span>
                  <span className="text-sm font-semibold text-ink-muted">/ 100</span>
                </div>
              </div>

              <div className="text-left sm:text-right">
                <span className="text-xs font-bold uppercase tracking-wider text-ink-secondary block mb-1">
                  Level Kompetensi
                </span>
                <span className="inline-block px-3.5 py-1.5 rounded-lg text-sm font-black uppercase tracking-wider bg-primary text-white shadow-xs">
                  {latest.band || 'Belum Ditetapkan'}
                </span>
              </div>
            </div>

            {currentBandDef && (
              <div className="mt-4 pt-3 border-t border-primary/20 text-xs text-ink-secondary leading-relaxed">
                <strong className="text-ink font-semibold">Definisi Capaian: </strong>
                {currentBandDef.desc}
              </div>
            )}
          </div>

          {/* Keterangan Keaktifan: Kelas Intensif PELITA SLI */}
          <div className="mb-6 rounded-lg border border-border bg-subtle/30 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-2.5 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-base">🎓</span>
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  Keterangan Keaktifan: Kelas Intensif PELITA SLI (10 Sesi Aktivitas)
                </span>
              </div>
              {data.intensiveClass && (
                <span className="inline-block px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-primary text-white">
                  {data.intensiveClass.status === 'lulus' ? 'Lulus Pelatihan' : data.intensiveClass.status}
                </span>
              )}
            </div>

            {data.intensiveClass ? (
              <>
                <div className="grid grid-cols-3 gap-3 text-center text-xs">
                  <div className="rounded bg-white p-2.5 border border-border">
                    <div className="text-[10px] text-ink-muted uppercase">Tingkat Presensi</div>
                    <div className="text-base font-extrabold tabular text-ink mt-0.5">
                      {data.intensiveClass.attendancePct}%
                    </div>
                    <div className="text-[10px] text-ink-muted">10 Sesi Materi</div>
                  </div>
                  <div className="rounded bg-white p-2.5 border border-border">
                    <div className="text-[10px] text-primary uppercase font-semibold">Evaluasi Pre & Post-Test</div>
                    <div className="text-sm font-bold tabular text-ink mt-0.5">
                      {data.intensiveClass.preTestScore ?? '—'} ➔{' '}
                      <strong className="text-primary">{data.intensiveClass.postTestScore ?? '—'}</strong>
                    </div>
                    <div className="text-[10px] text-ink-muted">Skala 0 – 100</div>
                  </div>
                  <div className="rounded bg-white p-2.5 border border-border">
                    <div className="text-[10px] text-emerald-600 uppercase font-semibold">Peningkatan (Gain)</div>
                    <div className="text-base font-extrabold tabular text-emerald-700 mt-0.5">
                      {data.intensiveClass.gainScore !== null && data.intensiveClass.gainScore !== undefined
                        ? (data.intensiveClass.gainScore > 0
                            ? `+${data.intensiveClass.gainScore}`
                            : `${data.intensiveClass.gainScore}`)
                        : '—'}{' '}
                      Poin
                    </div>
                    <div className="text-[10px] text-ink-muted">Skor Peningkatan</div>
                  </div>
                </div>

                {/* Rincian Evaluasi 10 Sesi (jika ada) */}
                {data.intensiveClass.sessionScores && data.intensiveClass.sessionScores.length > 0 && (
                  <div className="mt-2.5 pt-2 border-t border-border/70">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-secondary mb-1.5">
                      Rincian Nilai Per Sesi Kelas Intensif:
                    </div>
                    <div className="grid grid-cols-5 gap-1.5 text-[10px]">
                      {data.intensiveClass.sessionScores.map((sc) => (
                        <div
                          key={sc.sessionNumber}
                          className="rounded bg-white p-1.5 border border-border/80 text-center"
                        >
                          <div className="font-bold text-ink">Sesi #{sc.sessionNumber}</div>
                          <div className="tabular text-ink-muted mt-0.5">
                            {sc.preTest ?? '—'} ➔ <strong className="text-primary">{sc.postTest ?? '—'}</strong>
                          </div>
                          {sc.gain !== null && sc.gain !== undefined && (
                            <div
                              className={`font-bold text-[9px] mt-0.5 ${
                                sc.gain > 0
                                  ? 'text-emerald-700'
                                  : sc.gain < 0
                                    ? 'text-rose-700'
                                    : 'text-gray-600'
                              }`}
                            >
                              {sc.gain > 0 ? `+${sc.gain}` : sc.gain}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {data.intensiveClass.notes && (
                  <div className="mt-2.5 text-[11px] text-ink-secondary italic pt-1 border-t border-border/60">
                    &ldquo;{data.intensiveClass.notes}&rdquo;
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-ink-muted italic">
                Data keaktifan Kelas Intensif belum terekam di sistem.
              </p>
            )}
          </div>

          {/* Tabel Rincian Dimensi */}
          <div className="mb-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-ink border-b border-border pb-1.5 mb-3">
              Rincian Capaian per Dimensi Kompetensi
            </h3>
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-xs text-left">
                <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold">
                  <tr>
                    <th className="px-3 py-2 w-10 text-center">No</th>
                    <th className="px-3 py-2">Dimensi Kompetensi</th>
                    <th className="px-3 py-2 w-20 text-center">Bobot</th>
                    <th className="px-3 py-2 w-24 text-right">Skor Nilai</th>
                    <th className="px-3 py-2 w-32">Pencapaian</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dimensions.map((dim, idx) => {
                    const pct = Math.min(100, Math.max(0, dim.value));
                    return (
                      <tr key={dim.dimensionId} className="hover:bg-subtle/30">
                        <td className="px-3 py-2.5 text-center text-ink-muted font-medium">{idx + 1}</td>
                        <td className="px-3 py-2.5">
                          <strong className="font-semibold text-ink block">{dim.name}</strong>
                          <span className="text-[11px] text-ink-muted">{dim.code}</span>
                        </td>
                        <td className="px-3 py-2.5 text-center font-medium tabular text-ink-secondary">
                          {dim.weight}%
                        </td>
                        <td className="px-3 py-2.5 text-right font-bold text-ink tabular">
                          {dim.value.toFixed(1)}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-border overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="w-9 text-right font-semibold tabular text-[11px] text-ink-secondary">
                              {pct.toFixed(0)}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Riwayat Antar Semester (Tren) jika ada */}
          {trend.length > 1 && (
            <div className="mb-6">
              <h3 className="text-xs font-bold uppercase tracking-wider text-ink border-b border-border pb-1.5 mb-3">
                Riwayat Perkembangan Kompetensi Antar Semester
              </h3>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-xs text-left">
                  <thead className="bg-subtle text-ink-secondary border-b border-border font-semibold">
                    <tr>
                      <th className="px-3 py-2">Semester / Periode</th>
                      <th className="px-3 py-2 text-center">Tahun Program</th>
                      <th className="px-3 py-2 text-right">Indeks Akhir</th>
                      <th className="px-3 py-2">Kategori Capaian</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {trend.map((t, idx) => (
                      <tr key={idx} className={t.periodName === latest.periodName ? 'bg-primary-soft/20 font-medium' : ''}>
                        <td className="px-3 py-2">
                          {t.periodName}
                          {t.periodName === latest.periodName && (
                            <span className="ml-2 text-[10px] uppercase font-bold text-primary">(Saat Ini)</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center text-ink-secondary">
                          {t.programYear ? `Tahun ${t.programYear}` : '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-bold tabular">
                          {t.index !== null ? t.index.toFixed(1) : '—'}
                        </td>
                        <td className="px-3 py-2 font-semibold text-primary">{t.band || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Standar Level Kompetensi */}
          <div className="mb-8">
            <h3 className="text-xs font-bold uppercase tracking-wider text-ink border-b border-border pb-1.5 mb-3">
              Standar Definisi Level Kompetensi PELITA SLI
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              {bandList.map((b) => {
                const isCurrent = b.name.toLowerCase() === latest.band?.toLowerCase();
                return (
                  <div
                    key={b.name}
                    className={`rounded-md p-2.5 border ${
                      isCurrent
                        ? 'border-primary bg-primary-soft/30 font-medium shadow-2xs'
                        : 'border-border bg-subtle/20 text-ink-secondary'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className={`font-bold text-xs ${isCurrent ? 'text-primary' : 'text-ink'}`}>
                        {b.name} {isCurrent && '★ (Posisi Saat Ini)'}
                      </span>
                      <span className="font-semibold text-ink-muted tabular">{b.range}</span>
                    </div>
                    <p className="text-[11px] leading-relaxed text-ink-secondary">{b.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
